import { useCallback, useMemo, useState } from "react";
import { DYNAMIC_ISLAND } from "../../../config/dynamicIsland.configuration";
import { type ItineraryDTO, type ItineraryLegDTO } from "../../../types/dtos/api/itinerary/itinerary.dto";
import { type VehicleSelectionInfo } from "../../MapLibre/vehicleSelection/vehicleSelection.types";
import { type DynamicIslandHostContext } from "../dynamicIsland.registry";
import { type DynamicIslandPayload } from "../dynamicIsland.types";
import { type DynamicIslandSlotProps } from "../DynamicIslandSlot";
import { mapItineraryToDynamicIslandPayload } from "../variants/itinerarySummary/itinerarySummary.mapper";
import { mapVehicleSelectionToDynamicIslandPayload } from "../variants/vehicleRealtime/vehicleRealtime.mapper";

/** Camera updater signature exposed to variants via {@link DynamicIslandHostContext}. */
export type DynamicIslandUpdateCamera = DynamicIslandHostContext["updateCamera"];

interface UseDynamicIslandOrchestratorParams {

    /** Whether dark UI palette is active. */
    "isDarkUi": boolean;

    /** Map camera updater from useMapNavigation. */
    "updateCamera": DynamicIslandUpdateCamera;

    /** Active public-transport itinerary from Home, if any. */
    "selectedItinerary": ItineraryDTO | null;

    /** Active car-mode itinerary (`ITakeMyCarData`), if any. */
    "carItineraryData": ItineraryDTO | null;

    /** Currently highlighted leg, if any. */
    "selectedLeg": ItineraryLegDTO | null;

    /** Leg chip click handler for the itinerary summary variant. */
    "onItineraryLegClick": ( leg: ItineraryLegDTO ) => void;

    /** Whether vehicle follow mode is active. */
    "isFollowingVehicle": boolean;

    /** Toggles vehicle follow mode from the vehicle realtime variant. */
    "toggleVehicleFollow": () => void;
}

interface UseDynamicIslandOrchestratorResult {

    /** Active island payload (undefined when disabled or no source data). */
    "payload": DynamicIslandPayload | undefined;

    /** Props for DynamicIslandSlot in FloatingMenu. */
    "slotProps": DynamicIslandSlotProps;

    /** Callback wired to MapComponent onVehicleSelectionInfoChange. */
    "handleVehicleSelectionInfoChange": ( info: VehicleSelectionInfo | undefined ) => void;
}

/**
 * Orchestrates Dynamic Island state and map bridge callbacks.
 * Priority when multiple sources apply: vehicleRealtime > itinerarySummary
 * (a selected vehicle is the more specific context, and its chrome carries the
 * follow toggle).
 *
 * @param params Hook parameters.
 * @returns Island state and integration handles.
 */
export function useDynamicIslandOrchestrator( params: UseDynamicIslandOrchestratorParams ): UseDynamicIslandOrchestratorResult {
    const {
        isDarkUi,
        updateCamera,
        selectedItinerary,
        carItineraryData,
        selectedLeg,
        onItineraryLegClick,
        isFollowingVehicle,
        toggleVehicleFollow,
    } = params;
    const [ vehicleSelectionInfo, setVehicleSelectionInfo ] = useState<VehicleSelectionInfo | undefined>( undefined );

    const handleVehicleSelectionInfoChange = useCallback( ( info: VehicleSelectionInfo | undefined ): void => {
        setVehicleSelectionInfo( info );
    }, [] );

    const payload = useMemo( (): DynamicIslandPayload | undefined => {
        if ( !DYNAMIC_ISLAND.enabled ) {
            return undefined;
        }

        const itineraryPayload = mapItineraryToDynamicIslandPayload( selectedItinerary, carItineraryData, selectedLeg );
        const vehiclePayload = mapVehicleSelectionToDynamicIslandPayload( vehicleSelectionInfo );

        return vehiclePayload ?? itineraryPayload;
    }, [ carItineraryData, selectedLeg, selectedItinerary, vehicleSelectionInfo ] );

    const hostContext = useMemo<DynamicIslandHostContext>( () => ( {
        updateCamera,
        onItineraryLegClick,
        isFollowingVehicle,
        toggleVehicleFollow,
    } ), [ isFollowingVehicle, onItineraryLegClick, toggleVehicleFollow, updateCamera ] );

    const slotProps = useMemo<DynamicIslandSlotProps>( () => ( {
        payload,
        isDarkUi,
        hostContext,
    } ), [ hostContext, isDarkUi, payload ] );

    return {
        payload,
        slotProps,
        handleVehicleSelectionInfoChange,
    };
}
